import { useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';
import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { Skeleton } from '@ifixit/ui/components/skeleton';
import { cn } from '@ifixit/ui/lib/utils';
import { imgSrcSet } from 'Guide/image_srcset';
import type { ResponsiveImageProps } from 'Guide/image_srcset';
import { ImageLightbox } from 'Shared/ImageLightbox';
import type { StepImage } from './types';

// api/2.0 carries a url per size and no dimensions, so a photo's real box is
// unknown until it decodes. 4:3 is what renditions are fitted into.
const FALLBACK_RATIO = '4 / 3';

/**
 * A step's image media: one large image with a thumbnail row that switches it on
 * hover (or tap), and click-to-zoom into the shared ImageLightbox. Parity with
 * the image branch of embed.v5.steps.js — the vanilla hover-switch, minus the
 * FLIP transform (a plain lightbox reaches the same enlarged view).
 */
export function StepImageGallery({
   images,
   onImageChange,
}: {
   images: StepImage[];
   // Notified when the shown image changes (drives the FixBot bridge). Fires only
   // on an actual change, not for the initial image — that's reported on step
   // entry via the bridge's stepChange.
   onImageChange?: (imageIndex: number) => void;
}) {
   const [active, setActive] = useState(0);
   // The rendition the browser actually picked from the set below, read
   // when the reader opens the lightbox: src is only the fallback, and on
   // most viewports the browser takes something smaller.
   const shown = useRef<HTMLImageElement>(null);
   const [zoomed, setZoomed] = useState<{ placeholder?: string } | null>(null);
   // Kept through a thumbnail switch rather than reset: the frame is about to
   // be filled again, and collapsing it would reflow the screen twice.
   const [ratio, setRatio] = useState(FALLBACK_RATIO);
   const [loaded, setLoaded] = useState(false);

   const current = images[active] ?? images[0];
   const large = imgSrcSet(current, '50vw', 'large');

   const selectImage = (index: number) => {
      if (index === active) {
         return;
      }
      setActive(index);
      setLoaded(false);
      onImageChange?.(index);
   };

   const onLargeLoad = (event: SyntheticEvent<HTMLImageElement>) => {
      const img = event.currentTarget;
      if (img.naturalWidth && img.naturalHeight) {
         setRatio(`${img.naturalWidth} / ${img.naturalHeight}`);
      }
      setLoaded(true);
   };

   // A photo that fails to load never fires onLoad, and the skeleton under it
   // would pulse for as long as the step is open.
   const onLargeError = () => setLoaded(true);

   return (
      <div className="text-center">
         <button
            type="button"
            onClick={() => setZoomed({ placeholder: shown.current?.currentSrc })}
            className="group relative block w-full cursor-zoom-in overflow-hidden rounded-2xl border border-gray-300"
            aria-label={_js('Zoom image')}
         >
            <div className="relative max-h-[65vh] w-full" style={{ aspectRatio: ratio }}>
               {!loaded && large.src && (
                  <Skeleton className="absolute inset-0 size-full rounded-none" />
               )}
               <img
                  ref={shown}
                  className="absolute inset-0 size-full object-contain"
                  src={large.src}
                  srcSet={large.srcSet}
                  sizes={large.sizes}
                  onLoad={onLargeLoad}
                  onError={onLargeError}
                  alt=""
               />
            </div>
            {/* Zoom affordance: a magnifier badge that fades in on hover. */}
            <span className="pointer-events-none absolute bottom-3 right-3 flex size-9 items-center justify-center rounded-full bg-black/55 text-white opacity-0 transition-opacity group-hover:opacity-100">
               <FaIcon name="magnifying-glass-plus" aria-hidden />
            </span>
         </button>

         {images.length > 1 && (
            <div className="mt-3 flex flex-wrap gap-3">
               {images.map((img, i) => {
                  const thumb = imgSrcSet(img, '20vw', 'standard');
                  return (
                     <button
                        type="button"
                        key={img.id ?? i}
                        onMouseEnter={() => selectImage(i)}
                        onFocus={() => selectImage(i)}
                        onClick={() => selectImage(i)}
                        className={cn(
                           // A 2px edge in both states: at 1px the selected thumb
                           // vanishes against a busy photo, and an equal width in
                           // both states keeps the row from reflowing on select.
                           'w-[calc((100%-1.5rem)/3)] overflow-hidden rounded-2xl border-2 transition-colors',
                           i === active ? 'border-gray-500' : 'border-gray-300'
                        )}
                        aria-label={_js('Show image %1', String(i + 1))}
                        aria-pressed={i === active}
                     >
                        <Thumbnail thumb={thumb} />
                     </button>
                  );
               })}
            </div>
         )}

         {zoomed && (
            <StepImageLightbox
               image={current}
               placeholder={zoomed.placeholder}
               onClose={() => setZoomed(null)}
            />
         )}
      </div>
   );
}

// Thumbs at their own heights change the row's height as each one arrives, so
// they take a 4:3 box and object-cover, as the guide editor's rows do.
function Thumbnail({ thumb }: { thumb: ResponsiveImageProps }) {
   const [loaded, setLoaded] = useState(false);

   return (
      <div className="relative aspect-[4/3] w-full">
         {!loaded && thumb.src && <Skeleton className="absolute inset-0 size-full rounded-none" />}
         <img
            className="absolute inset-0 size-full object-cover"
            src={thumb.src}
            srcSet={thumb.srcSet}
            sizes={thumb.sizes}
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
            alt=""
         />
      </div>
   );
}

function StepImageLightbox({
   image,
   placeholder,
   onClose,
}: {
   image: StepImage;
   placeholder?: string;
   onClose: () => void;
}) {
   const full = imgSrcSet(image, '95vw', 'huge');
   if (!full.src) {
      return null;
   }
   return (
      <ImageLightbox
         url={full.src}
         srcSet={full.srcSet}
         sizes={full.sizes}
         label={_js('Step image')}
         placeholder={placeholder}
         onClose={onClose}
      />
   );
}
